import React, { useEffect, useState } from 'react';
import SearchableSelect from './SearchableSelect';
import { fetchBranches, fetchGroups, fetchTickets, fetchRecord } from '../api/connectionRegistryApi';
// This selector reports inline, in the slot under each dropdown, because it is
// embedded in screens that own their own toast and would otherwise fire two.
import { NO_DATA_MESSAGE, isEmptyResult, inlineNoticeColor } from '../utils/noData';

const errorStyle = (msg) => ({ fontSize: '11px', color: inlineNoticeColor(msg), marginTop: '4px' });

/** Section A of every change-request form: Branch -> Group -> Ticket cascade.
 * Resolving a ticket loads the member record for the form below, both via
 * the connection's own EasyChit instance (proxied through the ITSM backend). */
// `onSelectionChange` is optional and reports the branch / group / ticket behind
// the loaded record. Screens that need to call other endpoints keyed on those
// values (Change Name) take it; the rest only ever want the record itself.
function TicketSelector({ connectionId, onRecordChange, onSelectionChange }) {
  const reportSelection = (sel) => {
    if (onSelectionChange) onSelectionChange(sel);
  };
  const [branches, setBranches] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tickets, setTickets] = useState([]);

  const [branchCode, setBranchCode] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const [ticketNo, setTicketNo] = useState('');

  const [loading, setLoading] = useState({ branches: false, groups: false, tickets: false, record: false });
  const [error, setError] = useState({ branches: '', groups: '', tickets: '', record: '' });

  useEffect(() => {
    setBranches([]); setGroups([]); setTickets([]);
    setBranchCode(''); setGroupCode(''); setTicketNo('');
    onRecordChange(null);
    reportSelection(null);
    if (!connectionId) return;

    (async () => {
      setLoading((l) => ({ ...l, branches: true }));
      setError((e) => ({ ...e, branches: '' }));
      try {
        setBranches(await fetchBranches(connectionId));
      } catch (e) {
        setError((er) => ({ ...er, branches: 'Could not load branches from the client API' }));
      } finally {
        setLoading((l) => ({ ...l, branches: false }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId]);

  const group = groups.find((g) => g.group_Code === groupCode);

  const handleBranch = async (value) => {
    setBranchCode(value);
    setGroupCode(''); setTicketNo('');
    setGroups([]); setTickets([]);
    onRecordChange(null);
    reportSelection(null);
    if (!value) return;

    setLoading((l) => ({ ...l, groups: true }));
    setError((e) => ({ ...e, groups: '' }));
    try {
      const list = await fetchGroups(connectionId, value);
      setGroups(list);
      if (isEmptyResult(list)) setError((er) => ({ ...er, groups: NO_DATA_MESSAGE }));
    } catch (e) {
      setError((er) => ({ ...er, groups: 'Could not load groups for that branch' }));
    } finally {
      setLoading((l) => ({ ...l, groups: false }));
    }
  };

  const handleGroup = async (value) => {
    setGroupCode(value);
    setTicketNo('');
    setTickets([]);
    onRecordChange(null);
    reportSelection(null);
    if (!value) return;

    setLoading((l) => ({ ...l, tickets: true }));
    setError((er) => ({ ...er, tickets: '' }));
    try {
      const list = await fetchTickets(connectionId, branchCode, value);
      setTickets(list);
      if (isEmptyResult(list)) setError((er) => ({ ...er, tickets: NO_DATA_MESSAGE }));
    } catch (er) {
      setError((e) => ({ ...e, tickets: 'Could not load tickets for that group' }));
    } finally {
      setLoading((l) => ({ ...l, tickets: false }));
    }
  };

  const handleTicket = async (value) => {
    setTicketNo(value);
    onRecordChange(null);
    reportSelection(null);
    if (!value || !group) return;

    setLoading((l) => ({ ...l, record: true }));
    setError((er) => ({ ...er, record: '' }));
    try {
      const record = await fetchRecord(connectionId, branchCode, Number(value), Number(group.chitgroup_id));
      if (isEmptyResult(record)) setError((er) => ({ ...er, record: NO_DATA_MESSAGE }));
      onRecordChange({ ...record, branchCode });
      reportSelection({
        branchCode,
        groupCode,
        chitgroupId: Number(group.chitgroup_id),
        ticketNo: Number(value),
      });
    } catch (er) {
      setError((e) => ({ ...e, record: 'Could not load the member record for that ticket' }));
    } finally {
      setLoading((l) => ({ ...l, record: false }));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <div style={{
          width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white',
          fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>A</div>
        <div>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
          <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group and ticket to load member details</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Branch (CAO) *</label>
          <SearchableSelect
            options={branches}
            value={branchCode}
            onChange={handleBranch}
            getLabel={(b) => b.branch_name}
            getValue={(b) => b.branch_code}
            placeholder={loading.branches ? 'Loading branches...' : 'Search branch...'}
            testId="ticket-selector-branch"
          />
          {error.branches && <div style={errorStyle(error.branches)}>{error.branches}</div>}
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Group Code *</label>
          <SearchableSelect
            options={groups}
            value={groupCode}
            onChange={handleGroup}
            getLabel={(g) => g.group_Code}
            getValue={(g) => g.group_Code}
            placeholder={loading.groups ? 'Loading...' : 'Search group code...'}
            disabled={!branchCode || loading.groups}
            testId="ticket-selector-group"
          />
          {error.groups && <div style={errorStyle(error.groups)}>{error.groups}</div>}
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Ticket No *</label>
          <SearchableSelect
            options={tickets}
            value={ticketNo}
            onChange={handleTicket}
            getLabel={(t) => t.ticketno}
            getValue={(t) => String(t.ticketno)}
            placeholder={loading.tickets ? 'Loading...' : 'Search ticket no...'}
            disabled={!groupCode || loading.tickets}
            testId="ticket-selector-ticket"
          />
          {error.tickets && <div style={errorStyle(error.tickets)}>{error.tickets}</div>}
          {loading.record && <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>Loading record...</div>}
          {error.record && <div style={errorStyle(error.record)}>{error.record}</div>}
        </div>
      </div>
    </div>
  );
}

export default TicketSelector;
